// src/components/patient/appointments/PatientVideoPanel.jsx
//
// The patient's side of a video consultation, on the existing Twilio Video
// integration. Adapted from the Doctor portal's VideoConsultationPanel: the
// token contract is identical ({ token, room_unique_name }), but the copy,
// participant wording and event reporting are patient-specific, so this is a
// patient copy rather than a byte-shared module.
//
// CREDENTIALS. A token is requested immediately before each connect and lives
// only in a local variable. It is never put in React state, the URL, web
// storage, logs or the DOM. If Twilio reports the token expired (20104) the
// panel reconnects once with a fresh one.
//
// LIFECYCLE. One room at a time; leaving or unmounting disconnects the room,
// stops the camera and microphone and removes attached media. Leaving never
// cancels or completes the appointment.

import React, { useCallback, useEffect, useRef, useState } from "react";

import { readApiError } from "../../../lib/apiErrors";

const TOKEN_EXPIRED_CODE = 20104;

export const VIDEO_STATE_LABELS = {
  idle: "Not connected",
  connecting: "Connecting…",
  connected: "Connected",
  reconnecting: "Reconnecting…",
  disconnected: "Disconnected",
  error: "Video unavailable",
};

export function deviceGuidance(error) {
  const name = error?.name || "";
  if (name === "NotAllowedError" || name === "PermissionDeniedError") {
    return "Camera or microphone access was blocked. Allow access in your browser's site settings, then retry.";
  }
  if (name === "NotFoundError" || name === "DevicesNotFoundError") {
    return "No camera or microphone was found. Connect a device, then retry.";
  }
  if (name === "NotReadableError") {
    return "Your camera or microphone is in use by another application. Close it, then retry.";
  }
  return "The video call could not be started. Check your connection and retry.";
}

export default function PatientVideoPanel({ appointmentId, api, canJoin, unavailableMessage, loadVideo = () => import("twilio-video") }) {
  const [state, setState] = useState("idle");
  const [message, setMessage] = useState("");
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [remoteCount, setRemoteCount] = useState(0);

  const roomRef = useRef(null);
  const joiningRef = useRef(false);
  const mountedRef = useRef(true);
  const refreshedRef = useRef(false);
  const localRef = useRef(null);
  const remoteRef = useRef(null);

  const report = useCallback(
    (eventType) => {
      api.recordVideoEvent?.(appointmentId, eventType)?.catch?.(() => {});
    },
    [api, appointmentId]
  );

  const teardown = useCallback(() => {
    const room = roomRef.current;
    roomRef.current = null;
    if (room) {
      room.removeAllListeners?.();
      room.localParticipant?.tracks?.forEach?.((publication) => {
        publication.track?.stop?.();
        publication.track?.detach?.()?.forEach?.((el) => el.remove());
      });
      room.disconnect?.();
    }
    [localRef.current, remoteRef.current].forEach((node) => {
      if (node) node.innerHTML = "";
    });
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (roomRef.current) report("left");
      teardown();
    };
  }, [teardown, report]);

  const attachTrack = (track, container) => {
    if (!container || !track?.attach) return;
    const element = track.attach();
    element.setAttribute?.("data-track-kind", track.kind);
    container.appendChild(element);
  };

  const join = useCallback(async () => {
    if (joiningRef.current || roomRef.current) return;
    joiningRef.current = true;
    setState("connecting");
    setMessage("");
    try {
      let credential;
      try {
        credential = await api.createVideoToken(appointmentId);
      } catch (err) {
        if (!mountedRef.current) return;
        setState("error");
        setMessage(readApiError(err, "Video is not available right now."));
        return;
      }
      const Video = await loadVideo();
      const room = await Video.connect(credential.token, { name: credential.room_unique_name, audio: true, video: { width: 640 } });
      credential = null;
      if (!mountedRef.current) {
        room.disconnect();
        return;
      }
      roomRef.current = room;
      setMicOn(true);
      setCamOn(true);
      report("joined");

      room.localParticipant.tracks.forEach((publication) => {
        if (publication.track?.kind === "video") attachTrack(publication.track, localRef.current);
      });
      const onParticipant = (participant) => {
        setRemoteCount(room.participants.size);
        participant.tracks.forEach((publication) => {
          if (publication.track) attachTrack(publication.track, remoteRef.current);
        });
        participant.on("trackSubscribed", (track) => attachTrack(track, remoteRef.current));
        participant.on("trackUnsubscribed", (track) => track.detach().forEach((el) => el.remove()));
      };
      room.participants.forEach(onParticipant);
      room.on("participantConnected", onParticipant);
      room.on("participantDisconnected", () => setRemoteCount(room.participants.size));
      room.on("reconnecting", () => {
        if (!mountedRef.current) return;
        setState("reconnecting");
        report("reconnecting");
      });
      room.on("reconnected", () => mountedRef.current && setState("connected"));
      room.on("disconnected", (_room, error) => {
        if (!mountedRef.current) return;
        teardown();
        if (error?.code === TOKEN_EXPIRED_CODE && !refreshedRef.current) {
          refreshedRef.current = true;
          setState("reconnecting");
          setMessage("Refreshing your secure video session…");
          joiningRef.current = false;
          join();
          return;
        }
        setState(error ? "error" : "disconnected");
        setMessage(error ? "The call ended unexpectedly. You can retry." : "You left the video call. Your appointment is unchanged.");
      });
      setState("connected");
    } catch (error) {
      teardown();
      if (!mountedRef.current) return;
      if (error?.name && /NotAllowed|PermissionDenied|NotFound|DevicesNotFound|NotReadable/.test(error.name)) report("media_error");
      setState("error");
      setMessage(deviceGuidance(error));
    } finally {
      joiningRef.current = false;
    }
  }, [api, appointmentId, teardown, report, loadVideo]);

  const leave = () => {
    teardown();
    report("left");
    setState("disconnected");
    setMessage("You left the video call. Your appointment is unchanged.");
  };

  const toggle = (kind) => {
    const room = roomRef.current;
    if (!room) return;
    const next = kind === "audio" ? !micOn : !camOn;
    room.localParticipant.tracks.forEach((publication) => {
      if (publication.track?.kind === kind) {
        if (next) publication.track.enable();
        else publication.track.disable();
      }
    });
    if (kind === "audio") setMicOn(next);
    else setCamOn(next);
  };

  const inCall = state === "connected" || state === "reconnecting";

  return (
    <div className="space-y-3" data-testid="video-panel">
      <p role="status" aria-live="polite" className="text-sm font-semibold text-slate-800" data-testid="video-state">
        {VIDEO_STATE_LABELS[state]}
        {inCall && remoteCount === 0 ? " · Waiting for your doctor" : ""}
      </p>
      {!canJoin && !inCall && unavailableMessage ? <p className="text-xs text-slate-600">{unavailableMessage}</p> : null}
      {message ? (
        <p role={state === "error" ? "alert" : undefined} className={`text-xs ${state === "error" ? "text-rose-700" : "text-slate-600"}`} data-testid="video-message">
          {message}
        </p>
      ) : null}

      <div className={`grid gap-2 sm:grid-cols-2 ${inCall ? "" : "hidden"}`}>
        <div ref={remoteRef} aria-label="Doctor video" className="aspect-video max-w-full overflow-hidden rounded-2xl bg-slate-900" />
        <div ref={localRef} aria-label="Your video" className="aspect-video max-w-full overflow-hidden rounded-2xl bg-slate-800" />
      </div>

      <div className="flex flex-wrap gap-2" role="group" aria-label="Video controls">
        {!inCall ? (
          <button type="button" onClick={join} disabled={!canJoin || state === "connecting"} aria-busy={state === "connecting" || undefined}
            className="rounded-2xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
            {state === "error" || state === "disconnected" ? "Retry video" : state === "connecting" ? "Connecting…" : "Join video"}
          </button>
        ) : (
          <>
            <button type="button" aria-pressed={!micOn} onClick={() => toggle("audio")} className="rounded-2xl border border-slate-300 px-3 py-2 text-sm font-semibold">
              {micOn ? "Mute microphone" : "Unmute microphone"}
            </button>
            <button type="button" aria-pressed={!camOn} onClick={() => toggle("video")} className="rounded-2xl border border-slate-300 px-3 py-2 text-sm font-semibold">
              {camOn ? "Turn camera off" : "Turn camera on"}
            </button>
            <button type="button" onClick={leave} className="rounded-2xl bg-rose-600 px-3 py-2 text-sm font-semibold text-white">
              Leave
            </button>
          </>
        )}
      </div>
    </div>
  );
}
